import { StandardEvents } from '@shopify/events';
import { formatMoney as formatThemeMoney } from '@theme/money-formatting';

const DESKTOP_QUERY = '(min-width: 990px)';
const CLOSE_DURATION = 230;
const DRAWER_EXIT_DURATION = 170;
const CARD_EXIT_DURATION = 260;
const RECOMMENDATIONS_PER_SEED = 10;
const RECOMMENDATION_LIMIT = 12;
const RECOMMENDATION_POOL_LIMIT = 18;
const CATALOG_LIMIT = 24;
const REQUEST_TIMEOUT = 8000;
const POOL_STORAGE_KEY = 'mineralia-cart-upsell-pool';
const POOL_STORAGE_TTL = 10 * 60 * 1000;
const CARD_IMAGE_WIDTH = 240;

class CartUpsell extends HTMLElement {
  #abortController = new AbortController();
  #cache = new Map();
  /** @type {Map<string, Promise<any[]>>} */
  #poolCache = new Map();
  #catalogPromise = null;
  #cartProductIds = new Set();
  #lastProductId = '';
  #drawerPanel = null;
  #drawerAnimationTimer = null;
  #closing = false;

  connectedCallback() {
    this.#lastProductId = this.dataset.initialProductId || '';
    this.#cartProductIds = new Set((this.dataset.cartProductIds || '').split(',').filter(Boolean));

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate, {
      signal: this.#abortController.signal,
    });
    document.addEventListener('theme-drawer:open', this.#onDrawerOpen, {
      signal: this.#abortController.signal,
    });
    document.addEventListener('theme-drawer:close', this.#onDrawerClose, {
      signal: this.#abortController.signal,
    });

    // The desktop recommendation panel is moved beside the drawer dialog, outside
    // this custom element. Delegate clicks from the document so both placements
    // share the same reliable add-to-cart behavior.
    document.addEventListener('click', this.#onClick, { signal: this.#abortController.signal });
    this.popup?.addEventListener('cancel', this.#onCancel, { signal: this.#abortController.signal });
    this.popup?.addEventListener('click', this.#onBackdropClick, { signal: this.#abortController.signal });

    // Warm the recommendations while the page is idle so the drawer panel and
    // the popup open with their products already loaded.
    if (this.#lastProductId) {
      const idle = window.requestIdleCallback ?? ((callback) => window.setTimeout(callback, 1200));
      idle(() => this.#warmRecommendations(this.#lastProductId), { timeout: 2500 });
    }
  }

  /** @param {string} productId */
  async #warmRecommendations(productId) {
    try {
      const products = await this.#getRecommendationPool(productId);
      products.slice(0, 6).forEach((product) => {
        const image = new Image();
        image.decoding = 'async';
        image.src = this.#productImageUrl(product);
      });
    } catch {
      /* Recommendations load again when the cart opens. */
    }
  }

  disconnectedCallback() {
    window.clearTimeout(this.#drawerAnimationTimer);
    this.#abortController.abort();
  }

  get popup() {
    return this.querySelector('[data-cart-upsell-popup]');
  }

  #installDrawerPanel() {
    const dialog = document.querySelector('#cart-drawer .theme-drawer__dialog');
    const template = this.querySelector('[data-drawer-recommendations-template]');
    if (!(dialog instanceof HTMLDialogElement) || !(template instanceof HTMLTemplateElement)) return;

    const existing = dialog.querySelector('.cart-drawer-recommendations');
    if (existing instanceof HTMLElement) {
      this.#drawerPanel = existing;
      return;
    }

    const panel = template.content.firstElementChild?.cloneNode(true);
    if (!(panel instanceof HTMLElement)) return;

    dialog.prepend(panel);
    this.#drawerPanel = panel;
  }

  #onCartLinesUpdate = (event) => {
    const sourceModal =
      event.target instanceof Element ? event.target.closest('dialog:modal') : null;

    event.promise
      ?.then(({ cart, detail }) => {
        if (detail?.didError) return;

        const items = Array.isArray(detail?.items) ? detail.items : [];
        if (event.context !== 'dialog') {
          this.#syncCart(items, cart?.totalQuantity ?? detail?.itemCount);
        }

        if (event.action !== 'add' || event.context === 'dialog') return;

        const variantId = String(event.lines?.[0]?.merchandiseId || '');
        const productId = String(detail?.productId || '');
        const addedItem =
          items.find((item) => String(item.variant_id) === variantId) ||
          items.find((item) => String(item.product_id) === productId) ||
          items[0];

        this.#lastProductId = String(addedItem?.product_id || productId);
        if (!this.#lastProductId) return;

        // Start recommendations and image preparation immediately. The popup
        // still waits for the shipping progress feedback before it appears.
        const recommendationsPromise = this.#getRecommendationPool(this.#lastProductId);

        const show = async () => {
          this.#renderAddedProduct(addedItem);
          await Promise.all([
            this.#waitForProgressAnimation(),
            this.#loadPopupRecommendations(this.#lastProductId, recommendationsPromise),
          ]);
          this.#openPopup();
          if (window.matchMedia(DESKTOP_QUERY).matches) {
            this.#loadDrawerRecommendations(this.#lastProductId, recommendationsPromise);
          }
        };

        if (sourceModal instanceof HTMLDialogElement && sourceModal.open) {
          sourceModal.addEventListener('close', show, { once: true });
        } else {
          show();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-upsell] Cart update failed:', error);
      });
  };

  #waitForProgressAnimation() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return Promise.resolve();

    const progress = [...document.querySelectorAll('free-shipping-progress')].find(
      (element) => element instanceof HTMLElement && element.getClientRects().length > 0
    );

    return new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        window.clearTimeout(fallback);
        document.removeEventListener('free-shipping-progress:complete', onComplete);
        resolve();
      };
      const onComplete = (event) => {
        if (!progress || event.target === progress) finish();
      };
      // With a visible bar, allow for its fill (560 ms) plus the free-shipping celebration (650 ms).
      const fallback = window.setTimeout(finish, progress ? 1500 : 620);

      document.addEventListener('free-shipping-progress:complete', onComplete);
    });
  }

  #onDrawerOpen = (event) => {
    if (!(event.target instanceof HTMLElement) || event.target.id !== 'cart-drawer') return;
    if (!window.matchMedia(DESKTOP_QUERY).matches || !this.#lastProductId) return;

    this.#installDrawerPanel();
    window.clearTimeout(this.#drawerAnimationTimer);
    this.#drawerPanel?.classList.remove('is-exiting', 'is-closed');
    this.#drawerPanel?.classList.add('is-entering');
    this.#drawerAnimationTimer = window.setTimeout(() => {
      this.#drawerPanel?.classList.remove('is-entering');
    }, 340);
    this.#loadDrawerRecommendations(this.#lastProductId);
  };

  #onDrawerClose = (event) => {
    if (!(event.target instanceof HTMLElement) || event.target.id !== 'cart-drawer') return;
    if (!(this.#drawerPanel instanceof HTMLElement)) return;

    window.clearTimeout(this.#drawerAnimationTimer);
    this.#drawerPanel.classList.remove('is-entering');
    this.#drawerPanel.classList.add('is-exiting');
    this.#drawerAnimationTimer = window.setTimeout(() => {
      this.#drawerPanel?.classList.remove('is-exiting');
      this.#drawerPanel?.classList.add('is-closed');
    }, DRAWER_EXIT_DURATION);
  };

  #onCancel = (event) => {
    event.preventDefault();
    this.#closePopup();
  };

  #onBackdropClick = (event) => {
    if (event.target === this.popup) this.#closePopup();
  };

  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-upsell-close]')) {
      this.#closePopup();
      return;
    }

    // A product link inside the popup closes it first so the page change does not cut the animation.
    const link = target.closest('a[href]');
    if (
      link instanceof HTMLAnchorElement &&
      this.popup?.contains(link) &&
      !event.defaultPrevented &&
      event.button === 0 &&
      !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) &&
      link.target !== '_blank'
    ) {
      event.preventDefault();
      this.#closePopup().then(() => window.location.assign(link.href));
      return;
    }

    if (target.closest('[data-upsell-view-cart]')) {
      this.#closePopup().then(() => {
        if (window.Shopify?.actions?.openCart) {
          window.Shopify.actions.openCart();
        } else {
          window.location.href = window.Theme?.routes?.cart_url || '/cart';
        }
      });
      return;
    }

    const addButton = target.closest('[data-upsell-add]');
    if (addButton instanceof HTMLButtonElement) {
      this.#addRecommendation(addButton);
      return;
    }

    const products = this.querySelector('[data-popup-products]');
    if (!(products instanceof HTMLElement)) return;

    if (target.closest('[data-upsell-prev]')) {
      products.scrollBy({ left: -products.clientWidth, behavior: 'smooth' });
    }
    if (target.closest('[data-upsell-next]')) {
      products.scrollBy({ left: products.clientWidth, behavior: 'smooth' });
    }

    const drawerProducts = this.#drawerPanel?.querySelector('[data-drawer-products]');
    if (!(drawerProducts instanceof HTMLElement)) return;

    if (target.closest('[data-drawer-prev]')) {
      drawerProducts.scrollBy({ left: -drawerProducts.clientWidth, behavior: 'smooth' });
    }
    if (target.closest('[data-drawer-next]')) {
      drawerProducts.scrollBy({ left: drawerProducts.clientWidth, behavior: 'smooth' });
    }
  };

  /**
   * Disables each arrow when its direction has nothing left to show and hides
   * both when every product already fits.
   * @param {HTMLElement} container
   */
  #syncArrows(container) {
    const root = container.closest('.cart-upsell-popup__recommendations, .cart-drawer-recommendations');
    const prev = root?.querySelector('[data-upsell-prev], [data-drawer-prev]');
    const next = root?.querySelector('[data-upsell-next], [data-drawer-next]');
    if (!(prev instanceof HTMLButtonElement) || !(next instanceof HTMLButtonElement)) return;

    const maxScroll = container.scrollWidth - container.clientWidth;
    const arrows = prev.parentElement;
    if (arrows instanceof HTMLElement) arrows.hidden = maxScroll <= 1;
    prev.disabled = container.scrollLeft <= 1;
    next.disabled = container.scrollLeft >= maxScroll - 1;
  }

  /** @param {HTMLElement} container */
  #watchArrows(container) {
    if (container.dataset.arrowsWatched !== 'true') {
      container.dataset.arrowsWatched = 'true';
      container.addEventListener('scroll', () => this.#syncArrows(container), {
        passive: true,
        signal: this.#abortController.signal,
      });
    }
    requestAnimationFrame(() => this.#syncArrows(container));
  }

  #syncCart(items, totalQuantity) {
    this.#cartProductIds = new Set(items.map((item) => String(item.product_id)).filter(Boolean));
    const quantity = Number(totalQuantity) || items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    this.dataset.cartCount = String(quantity);
    this.querySelectorAll('[data-upsell-view-cart]').forEach((node) => {
      node.textContent = `Ver carrito (${quantity})`;
    });
  }

  #renderAddedProduct(item) {
    const container = this.querySelector('[data-added-product]');
    const image = this.querySelector('[data-added-image]');
    const link = this.querySelector('[data-added-link]');
    const title = this.querySelector('[data-added-title]');
    const price = this.querySelector('[data-added-price]');
    if (!(container instanceof HTMLElement)) return;

    const imageUrl = this.#normalizeImage(item?.image || item?.featured_image?.url || '');
    if (image instanceof HTMLImageElement) {
      image.src = imageUrl;
      image.alt = item?.product_title || item?.title || '';
      image.hidden = !imageUrl;
    }
    if (link instanceof HTMLAnchorElement) link.href = item?.url || '#';
    if (title) title.textContent = item?.product_title || item?.title || 'Producto agregado';
    if (price instanceof HTMLElement) {
      this.#renderPrice(price, Number(item?.final_price ?? item?.price ?? 0), Number(item?.original_price ?? 0));
    }

    container.hidden = false;
  }

  #openPopup() {
    const popup = this.popup;
    if (!(popup instanceof HTMLDialogElement)) return;

    this.#closing = false;
    popup.classList.remove('is-closing');
    if (!popup.open) popup.showModal();
    popup.classList.add('is-opening');
    popup.addEventListener('animationend', () => popup.classList.remove('is-opening'), { once: true });
    requestAnimationFrame(() => {
      popup.querySelector('[data-upsell-close]')?.focus({ focusVisible: false });
      // The list was rendered while the dialog was closed; measure it now that it has a size.
      const products = popup.querySelector('[data-popup-products]');
      if (products instanceof HTMLElement) this.#syncArrows(products);
    });
  }

  async #closePopup() {
    const popup = this.popup;
    if (!(popup instanceof HTMLDialogElement) || !popup.open || this.#closing) return;

    this.#closing = true;
    popup.classList.remove('is-opening');
    popup.classList.add('is-closing');
    await new Promise((resolve) => setTimeout(resolve, CLOSE_DURATION));
    popup.classList.remove('is-closing');
    popup.close();
    this.#closing = false;
  }

  async #getRecommendations(productId) {
    if (this.#cache.has(productId)) return this.#cache.get(productId);

    const base = this.dataset.recommendationsUrl || '/recommendations/products';
    const url = new URL(base.endsWith('.json') ? base : `${base}.json`, window.location.origin);
    url.searchParams.set('product_id', productId);
    url.searchParams.set('limit', String(RECOMMENDATIONS_PER_SEED));
    url.searchParams.set('intent', 'related');

    const promise = this.#fetchJSON(url, 'Recommendations request')
      .then((payload) => (Array.isArray(payload.products) ? payload.products : []));

    this.#cache.set(productId, promise);
    promise.catch(() => {
      if (this.#cache.get(productId) === promise) this.#cache.delete(productId);
    });
    return promise;
  }

  async #getCatalogProducts() {
    if (this.#catalogPromise) return this.#catalogPromise;

    const root = window.Shopify?.routes?.root || '/';
    const url = new URL(`${root}products.json`, window.location.origin);
    url.searchParams.set('limit', String(CATALOG_LIMIT));

    const promise = this.#fetchJSON(url, 'Catalog request')
      .then((payload) => (Array.isArray(payload.products) ? payload.products : []))
      .then((products) => products.map((product) => this.#normalizeCatalogProduct(product)));

    this.#catalogPromise = promise;
    promise.catch(() => {
      if (this.#catalogPromise === promise) this.#catalogPromise = null;
    });
    return promise;
  }

  async #getRecommendationPool(primaryProductId) {
    const seedIds = [...new Set([primaryProductId, ...this.#cartProductIds].filter(Boolean))].slice(0, 3);
    const key = seedIds.join(',');
    const filterForCart = (products) =>
      products.filter((product) => !this.#cartProductIds.has(String(product.id)));

    const stored = this.#readStoredPool(key);
    if (stored) return filterForCart(stored);

    if (!this.#poolCache.has(key)) {
      const promise = this.#fetchRecommendationPool(seedIds).then((products) => {
        this.#storePool(key, products);
        return products;
      });
      this.#poolCache.set(key, promise);
      promise.catch(() => {
        if (this.#poolCache.get(key) === promise) this.#poolCache.delete(key);
      });
    }

    return filterForCart(await this.#poolCache.get(key));
  }

  /** @param {string[]} seedIds */
  async #fetchRecommendationPool(seedIds) {
    // The catalog fallback starts at the same time instead of after the
    // recommendations, so a short list never costs a second round trip.
    const catalogPromise = this.#getCatalogProducts().catch((error) => {
      console.warn('[cart-upsell] Catalog fallback unavailable:', error);
      return [];
    });
    const recommendedGroups = await Promise.all(
      seedIds.map((productId) => this.#getRecommendations(productId).catch(() => []))
    );
    let products = this.#dedupeProducts(recommendedGroups.flat());

    if (products.length < RECOMMENDATION_POOL_LIMIT) {
      products = this.#dedupeProducts([...products, ...(await catalogPromise)]);
    }

    return products
      .filter((product) => this.#isProductAvailable(product))
      .slice(0, RECOMMENDATION_POOL_LIMIT + 6)
      .map((product) => this.#slimProduct(product));
  }

  /** Keeps only what the cards need, so the pool fits comfortably in sessionStorage. */
  #slimProduct(product) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    return {
      id: product.id,
      title: product.title,
      handle: product.handle,
      url: product.url || `/products/${product.handle || ''}`,
      available: product.available !== false,
      price: Number(product.price || 0),
      compare_at_price: Number(product.compare_at_price || 0),
      featured_image: this.#productImageUrl(product),
      variants: variants.map((variant) => ({ id: variant.id, available: variant.available !== false })),
    };
  }

  /** @param {string} key */
  #readStoredPool(key) {
    try {
      const stored = JSON.parse(window.sessionStorage.getItem(POOL_STORAGE_KEY) || 'null');
      if (!stored || stored.key !== key || Date.now() - stored.time > POOL_STORAGE_TTL) return null;
      return Array.isArray(stored.products) ? stored.products : null;
    } catch {
      return null;
    }
  }

  /**
   * @param {string} key
   * @param {any[]} products
   */
  #storePool(key, products) {
    try {
      window.sessionStorage.setItem(POOL_STORAGE_KEY, JSON.stringify({ key, time: Date.now(), products }));
    } catch {
      /* Storage can be full or blocked; the in-memory cache still works. */
    }
  }

  async #fetchJSON(url, label) {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort('timeout'), REQUEST_TIMEOUT);

    try {
      const response = await fetch(url, {
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`${label} failed: ${response.status}`);
      return await response.json();
    } finally {
      window.clearTimeout(timeout);
    }
  }

  #dedupeProducts(products) {
    const seen = new Set();
    return products.filter((product) => {
      const id = String(product?.id || '');
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }

  #normalizeCatalogProduct(product) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const normalizedVariants = variants.map((variant) => ({
      ...variant,
      available: variant.available !== false,
      price: Math.round(Number.parseFloat(variant.price || 0) * 100),
      compare_at_price: Math.round(Number.parseFloat(variant.compare_at_price || 0) * 100),
    }));
    const firstVariant = normalizedVariants.find((variant) => variant.available) || normalizedVariants[0];

    return {
      ...product,
      available: normalizedVariants.some((variant) => variant.available),
      featured_image: product?.featured_image || product?.images?.[0],
      price: Number(firstVariant?.price || 0),
      compare_at_price: Number(firstVariant?.compare_at_price || 0),
      url: product?.url || `/products/${product?.handle || ''}`,
      variants: normalizedVariants,
    };
  }

  #isProductAvailable(product) {
    if (!product || product.available === false) return false;
    const variants = Array.isArray(product.variants) ? product.variants : [];
    return variants.length === 0 || variants.some((variant) => variant.available !== false);
  }

  async #loadPopupRecommendations(productId, recommendationsPromise = null) {
    const section = this.querySelector('[data-popup-recommendations]');
    const container = this.querySelector('[data-popup-products]');
    const loading = this.querySelector('[data-upsell-loading]');
    if (!(section instanceof HTMLElement) || !(container instanceof HTMLElement)) return;

    section.hidden = true;
    if (loading instanceof HTMLElement) loading.hidden = false;

    try {
      const products = (await (recommendationsPromise || this.#getRecommendationPool(productId)))
        .filter((product) => !this.#cartProductIds.has(String(product.id)))
        .slice(0, RECOMMENDATION_LIMIT);
      this.#renderProducts(container, products, 'popup');
      section.hidden = products.length === 0;
    } catch (error) {
      console.warn('[cart-upsell] Recommendations unavailable:', error);
      section.hidden = true;
    } finally {
      if (loading instanceof HTMLElement) loading.hidden = true;
    }
  }

  async #loadDrawerRecommendations(productId, recommendationsPromise = null) {
    if (!(this.#drawerPanel instanceof HTMLElement)) this.#installDrawerPanel();
    const container = this.#drawerPanel?.querySelector('[data-drawer-products]');
    const loading = this.#drawerPanel?.querySelector('[data-drawer-loading]');
    if (!(container instanceof HTMLElement)) return;

    if (container.dataset.seedProductId === String(productId) && container.childElementCount > 0) {
      this.#drawerPanel.hidden = false;
      this.#drawerPanel.classList.remove('is-closed');
      container.hidden = false;
      if (loading instanceof HTMLElement) loading.hidden = true;
      return;
    }

    if (loading instanceof HTMLElement) loading.hidden = false;
    container.hidden = true;

    try {
      const products = (await (recommendationsPromise || this.#getRecommendationPool(productId)))
        .filter((product) => !this.#cartProductIds.has(String(product.id)))
        .slice(0, RECOMMENDATION_LIMIT);
      this.#renderProducts(container, products, 'drawer');
      container.dataset.seedProductId = String(productId);
      this.#drawerPanel.hidden = products.length === 0;
      this.#drawerPanel.classList.remove('is-closed');
      container.hidden = products.length === 0;
      this.#watchArrows(container);
    } catch (error) {
      console.warn('[cart-upsell] Drawer recommendations unavailable:', error);
      this.#drawerPanel.hidden = true;
    } finally {
      if (loading instanceof HTMLElement) loading.hidden = true;
    }
  }

  #renderProducts(container, products, placement) {
    container.classList.add('is-refreshing');
    container.replaceChildren(...products.map((product, index) => this.#createProductCard(product, index)));
    container.scrollLeft = 0;
    requestAnimationFrame(() => container.classList.remove('is-refreshing'));
    if (placement === 'popup') this.#watchArrows(container);
  }

  #createProductCard(product, index) {
    const card = document.createElement('div');
    card.className = 'cart-upsell-card';
    card.setAttribute('role', 'listitem');
    card.dataset.productId = String(product.id || '');
    card.style.setProperty('--upsell-index', String(Math.min(index, 5)));

    const imageLink = document.createElement('a');
    imageLink.className = 'cart-upsell-card__image-link';
    imageLink.href = product.url || `/products/${product.handle}`;
    // La foto repite el enlace del título: se puede pulsar, pero el teclado y los
    // lectores de pantalla sólo pasan por el título para no anunciar dos veces la pieza.
    imageLink.tabIndex = -1;
    imageLink.setAttribute('aria-hidden', 'true');

    const image = document.createElement('img');
    image.src = this.#productImageUrl(product);
    image.alt = '';
    image.width = 220;
    image.height = 220;
    image.decoding = 'async';
    image.loading = index < 2 ? 'eager' : 'lazy';
    image.fetchPriority = 'auto';
    imageLink.append(image);

    const title = document.createElement('a');
    title.className = 'cart-upsell-card__title';
    title.href = imageLink.href;
    title.textContent = product.title || '';

    const price = document.createElement('div');
    price.className = 'cart-upsell-price';
    this.#renderPrice(price, Number(product.price || 0), Number(product.compare_at_price || 0));

    const variant = product.variants?.find((item) => item.available);
    const button = document.createElement('button');
    button.className = 'cart-upsell-card__add';
    button.type = 'button';
    button.textContent = variant ? 'Añadir' : 'Agotado';
    button.disabled = !variant;
    if (variant) button.setAttribute('aria-label', `Añadir ${product.title || 'producto'} al carrito`);
    button.dataset.upsellAdd = '';
    button.dataset.variantId = String(variant?.id || '');
    button.dataset.productId = String(product.id || '');
    button.dataset.productTitle = product.title || '';

    card.append(imageLink, title, price, button);
    return card;
  }

  #renderPrice(container, price, compareAtPrice) {
    container.replaceChildren();
    const onSale = compareAtPrice > price && price > 0;

    // Igual que `snippets/price.liquid`: los lectores de pantalla oyen qué precio es cuál.
    const hiddenLabel = (text) => {
      const label = document.createElement('span');
      label.className = 'visually-hidden';
      label.textContent = `${text} `;
      return label;
    };

    if (onSale) {
      const compare = document.createElement('span');
      compare.className = 'cart-upsell-price__compare';
      compare.append(hiddenLabel('Precio habitual'), this.#formatMoney(compareAtPrice));
      container.append(compare);
    }

    const current = document.createElement('span');
    if (onSale) {
      current.className = 'cart-upsell-price__sale';
      current.append(hiddenLabel('Precio de oferta'));
    }
    current.append(this.#formatMoney(price));
    container.append(current);
  }

  async #addRecommendation(button) {
    const variantId = button.dataset.variantId;
    const productId = button.dataset.productId;
    if (!variantId || !window.Shopify?.actions?.updateCart) return;

    const matchingButtons = document.querySelectorAll(
      `[data-upsell-add][data-variant-id="${CSS.escape(variantId)}"]`
    );
    matchingButtons.forEach((match) => {
      match.disabled = true;
      match.textContent = 'Añadiendo...';
      match.setAttribute('aria-busy', 'true');
      match.closest('.cart-upsell-card')?.classList.add('is-adding');
    });

    let mutationSucceeded = false;

    try {
      await window.Shopify.actions.updateCart(
        { lines: [{ merchandiseId: variantId, quantity: 1 }] },
        { event: { context: 'dialog', detail: { source: 'cart-upsell', productId } } }
      );
      mutationSucceeded = true;
      this.#lastProductId = productId || this.#lastProductId;
      if (productId) this.#cartProductIds.add(String(productId));

      const optimisticCount = Number(this.dataset.cartCount || 0) + 1;
      this.dataset.cartCount = String(optimisticCount);
      matchingButtons.forEach((match) => {
        match.textContent = 'Agregado';
        match.removeAttribute('aria-busy');
        match.closest('.cart-upsell-card')?.classList.remove('is-adding');
      });
      this.querySelectorAll('[data-upsell-view-cart]').forEach((node) => {
        node.textContent = `Ver carrito (${optimisticCount})`;
      });
      this.#animateRecommendationAdded(productId);

      try {
        const root = window.Shopify?.routes?.root || '/';
        const cart = await this.#fetchJSON(new URL(`${root}cart.js`, window.location.origin), 'Cart refresh');
        this.#syncCart(cart.items || [], cart.item_count);
      } catch (error) {
        console.warn('[cart-upsell] Product was added, but cart reconciliation failed:', error);
        document
          .getElementById('shopify-section-cart-drawer-section')
          ?.setAttribute('data-cart-needs-refresh', '');
      }

      this.#getRecommendationPool(this.#lastProductId).catch(() => {});
    } catch (error) {
      console.warn('[cart-upsell] Could not add recommendation:', error);
      if (mutationSucceeded) return;
      matchingButtons.forEach((match) => {
        match.disabled = false;
        match.textContent = 'Intentar de nuevo';
        match.removeAttribute('aria-busy');
        match.closest('.cart-upsell-card')?.classList.remove('is-adding');
      });
    }
  }

  #animateRecommendationAdded(productId) {
    if (!productId) return;
    const cards = document.querySelectorAll(
      `.cart-upsell-card[data-product-id="${CSS.escape(String(productId))}"]`
    );
    cards.forEach((card) => card.classList.add('is-added'));

    window.setTimeout(() => {
      cards.forEach((card) => {
        const container = card.parentElement;
        card.remove();
        if (container instanceof HTMLElement) {
          // Removing a card reflows the pages; land on a whole page again.
          const page = container.clientWidth || 1;
          const maxScroll = Math.max(0, container.scrollWidth - page);
          container.scrollTo({
            left: Math.min(Math.round(container.scrollLeft / page) * page, maxScroll),
            behavior: 'smooth',
          });
          this.#syncArrows(container);
        }
      });
    }, CARD_EXIT_DURATION);
  }

  #formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || 'MXN';
    const format = this.dataset.moneyFormat;
    if (format) {
      try {
        return formatThemeMoney(Number(cents || 0), format, currency);
      } catch {
        /* fall back to Intl below */
      }
    }
    const amount = new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(cents || 0) / 100);
    return `${amount} ${currency}`;
  }

  #productImageUrl(product) {
    const featured = product?.featured_image;
    const firstImage = product?.images?.[0];
    return this.#normalizeImage(
      featured?.url || featured?.src || featured || firstImage?.url || firstImage?.src || firstImage || ''
    );
  }

  #normalizeImage(url) {
    if (!url) return '';
    if (typeof url === 'object') url = url.url || url.src || '';
    const absolute = String(url).startsWith('//') ? `https:${url}` : String(url);

    // Shopify CDN images: request a card-sized file instead of the original upload.
    try {
      const parsed = new URL(absolute, window.location.origin);
      if (parsed.hostname.endsWith('cdn.shopify.com') || parsed.pathname.startsWith('/cdn/shop/')) {
        parsed.searchParams.set('width', String(CARD_IMAGE_WIDTH));
      }
      return parsed.href;
    } catch {
      return absolute;
    }
  }
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}
